import {
  useEventsSearch,
  useRequiredConfig,
  BaseEvent,
  isPartialStoppage
} from '@domain/shifthandover'
import {useUpmPlantNow, useUpmPlantTimezone} from '@hconnect/upm'
import moment from 'moment-timezone'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'
import {getTypeSpecificValidatorConfig} from '../utils'

export const useTypeSpecificValidatorConfig = (
  plantId: string,
  item: Partial<BaseEvent>,
  isFunctionalLocationRequired?: boolean,
  skipOverlapValidation?: boolean
) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const {shifts} = useRequiredConfig(plantId)
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const stoppage = isPartialStoppage(item) ? item : undefined

  const memoizedEndDate = useMemo(() => {
    return stoppage?.stoppageEnd || moment.tz(timezone)
  }, [stoppage?.stoppageEnd, timezone])

  const currentStoppageId = stoppage?.id || stoppage?.originalSplitStoppageId

  const {data} = useEventsSearch(
    {
      plantId,
      shifts,
      getPlantNow,
      timezone,
      pageNumber: 0,
      // fetch one more event than needed to check if there is an existing event other than the one being edited (in case of edit mode)
      itemsPerPage: currentStoppageId ? 2 : 1,
      input: {
        timeRange: {
          startDate: stoppage?.stoppageStart
            ? moment(stoppage.stoppageStart).add(1, 'millisecond')
            : moment.tz(timezone),
          endDate: memoizedEndDate.clone().subtract(1, 'millisecond')
        },
        upmDepartmentIds: stoppage?.upmDepartmentId ? [stoppage.upmDepartmentId] : undefined
      }
    },
    {enabled: isPartialStoppage(item) && !!item.upmDepartmentId && !!item.stoppageStart}
  )

  return useMemo(() => {
    const existingEvent = (
      currentStoppageId
        ? data?.events?.filter((event) => event.id !== currentStoppageId)
        : data?.events
    )?.[0]
    return getTypeSpecificValidatorConfig(
      t,
      item.eventType,
      isFunctionalLocationRequired,
      existingEvent,
      skipOverlapValidation
    )
  }, [
    currentStoppageId,
    data?.events,
    t,
    item.eventType,
    isFunctionalLocationRequired,
    skipOverlapValidation
  ])
}
